'use client'

import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import InventoryBrowserSection from './InventoryBrowserSection'
import ContactFormSection from './ContactFormSection'
import { useInquiryStore } from '@/lib/stores/inquiry-store'
import type { InventoryItem } from '@/lib/api/silo-api/types'

interface PublicInventoryBrowserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function PublicInventoryBrowserModal({
  open,
  onOpenChange
}: PublicInventoryBrowserModalProps) {
  const { toast } = useToast()
  const { addItem, selectedItems } = useInquiryStore()
  const [shouldFetch, setShouldFetch] = useState(false)

  // ✅ OPTIMIZED: Only fetch inventory when modal opens (lazy loading)
  useEffect(() => {
    if (open && !shouldFetch) {
      setShouldFetch(true)
    }
  }, [open, shouldFetch])

  const handleAddProduct = (product: InventoryItem) => {
    // Check if already added
    const existing = selectedItems.find(item => item.product_id === product.id)
    if (existing) {
      toast({
        title: 'Product Already Added',
        description: `${product.name} is already in your inquiry list.`,
      })
      return
    }
    
    // Add to inquiry - pass the product directly, let the store handle transformation
    addItem(product)
    
    toast({
      title: '✓ Added to inquiry form below',
      description: `${product.name} has been added to your inquiry.`,
    })
  }

  const handleSubmitSuccess = () => {
    // Auto-close modal after successful submission
    setTimeout(() => {
      onOpenChange(false)
    }, 5000) // Close after 5 seconds to let user see success message
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        hideOverlay 
        className="w-[95vw] sm:w-[calc(100vw-32px)] max-w-[95vw] sm:max-w-[calc(100vw-32px)] max-h-[calc(100vh-60px)] sm:max-h-[calc(100vh-100px)] overflow-y-auto rounded-lg sm:rounded-2xl p-3 sm:p-6 bg-white/5 backdrop-blur-xl border border-white/20"
        style={{
          boxShadow: '0 8px 32px 0 rgba(15, 20, 25, 0.37), 0 0 30px rgba(215, 164, 73, 0.3)'
        }}
      >
        <DialogHeader className="pb-4">
          <DialogTitle className="text-2xl text-white flex items-center justify-between">
            <div className="bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent font-bold">
              Request Product Samples
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Browse our Live Resin products and request samples for evaluation. 
            Select products you're interested in and we'll arrange sample delivery with detailed COAs.
          </DialogDescription>
        </DialogHeader>

        {/* Three-column layout (stacked on mobile) */}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:gap-6 overflow-x-hidden">
          {/* Left and Middle panels - Product Browser with Terminal */}
          <div className="lg:col-span-2 min-w-0 overflow-hidden">
            <InventoryBrowserSection onAddProduct={handleAddProduct} shouldFetch={shouldFetch} />
          </div>
          
          {/* Right panel - Contact Form (hidden on mobile, shown in separate view) */}
          <div className="hidden lg:block lg:col-span-1 h-[70vh] overflow-hidden">
            <ContactFormSection onSubmitSuccess={handleSubmitSuccess} />
          </div>
          
          {/* Mobile Contact Form - shown below on mobile */}
          <div className="block lg:hidden min-w-0 overflow-hidden">
            <ContactFormSection onSubmitSuccess={handleSubmitSuccess} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}